/**
 * Org Settings — minimal page with just the organization profile + public portal URL.
 * Email/SMTP and other platform-wide integrations live under /platform/* (platform_admin only).
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SettingsPage() {
    const [org, setOrg] = useState(null);

    useEffect(() => {
        api.get("/organizations/me")
            .then((r) => setOrg(r.data))
            .catch((e) => toast.error(extractError(e)));
    }, []);

    const portalUrl = org?.slug ? `${window.location.origin}/p/${org.slug}` : "";
    const copyPortal = async () => {
        if (!portalUrl) return;
        try { await navigator.clipboard.writeText(portalUrl); toast.success("Public portal URL copied"); }
        catch { toast.error("Copy failed"); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Organization"
                title="Settings"
                description="Your organization profile and public portal link."
                testid="settings-header"
            />

            <div className="brut-section p-8 max-w-2xl" data-testid="org-settings">
                <div className="flex items-center gap-3 mb-6">
                    <div className="inline-flex border border-border bg-secondary p-2 rounded-sm">
                        <Buildings size={18} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                        <div className="label-overline">Organization</div>
                        <h2 className="text-xl font-bold tracking-tight">{org?.name || "—"}</h2>
                    </div>
                </div>

                {portalUrl && (
                    <div className="border border-border bg-secondary/40 rounded-sm p-4 mb-6" data-testid="org-portal-url-block">
                        <Label className="label-overline">Public portal URL</Label>
                        <p className="text-[11px] text-muted-foreground mt-1 mb-2">Share this link with anyone — it lists every public form for your organization.</p>
                        <div className="flex gap-2">
                            <Input readOnly value={portalUrl} className="rounded-sm font-mono text-xs h-9" data-testid="org-portal-url-input" />
                            <Button type="button" variant="outline" className="rounded-sm h-9" onClick={copyPortal} data-testid="copy-portal-url-btn">
                                <Copy size={14} className="mr-1" /> Copy
                            </Button>
                        </div>
                    </div>
                )}

                <dl className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-border pb-3"><dt className="label-overline">Slug</dt><dd className="font-mono text-xs">{org?.slug || "—"}</dd></div>
                    <div className="flex justify-between border-b border-border pb-3"><dt className="label-overline">ID</dt><dd className="font-mono text-xs">{org?.id || "—"}</dd></div>
                    <div className="flex justify-between"><dt className="label-overline">Created</dt><dd className="font-mono text-xs">{org?.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}</dd></div>
                </dl>

                <p className="text-xs text-muted-foreground mt-6 pt-6 border-t border-border">
                    Need to update email, billing or theme settings? Those are managed by your platform administrator.
                </p>
            </div>
        </PageContainer>
    );
}
